import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type Board, type CandidateSummary, type Job, type PipelineCard } from "../lib/api";
import { candidateLine } from "./Candidates";
import { formatDay } from "../lib/format";


const pad = (n: number) => String(n).padStart(2, "0");
const cardWhen = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });

/** Whole days since a moment; "today" for under a day. */
export function daysSince(iso: string, now = Date.now()): string {
  const days = Math.floor((now - Date.parse(iso)) / 86_400_000);
  return days <= 0 ? "today" : days === 1 ? "1 day" : `${days} days`;
}

/**
 * The job's pipeline as columns: Shortlisted, each interview stage, Placed. Cards move with
 * ← → buttons (keyboard and touch friendly). Rejected and withdrawn sit in a list below.
 */
export function PipelineBoard({ job, onCount }: { job: Job; onCount?: (cards: number) => void }) {
  const [board, setBoard] = useState<Board | null>(null);
  const load = useAction();
  const act = useAction();

  const reload = useCallback(
    () =>
      load.run(async () => {
        const b = await api.pipeline(job.id);
        setBoard(b);
        onCount?.(b.cards.length);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [job.id],
  );
  useEffect(() => void reload(), [reload]);

  if (!board) return <ErrorNotice error={load.error} />;

  // Column order: null (Shortlisted), then each stage.
  const columns: { id: string | null; name: string }[] = [{ id: null, name: "Shortlisted" }, ...board.stages];
  const active = board.cards.filter((c) => c.status === "active");
  const placed = board.cards.filter((c) => c.status === "placed");
  const out = board.cards.filter((c) => c.status === "rejected" || c.status === "withdrawn");

  const move = (card: PipelineCard, by: -1 | 1) => {
    const i = columns.findIndex((c) => c.id === card.stageId);
    const target = columns[i + by];
    if (!target) return;
    void act.run(async () => {
      await api.applicationAction(card.id, { kind: "move", stageId: target.id });
      await reload();
    });
  };

  const open = job.status === "open" || job.status === "on_hold";

  return (
    <section className="section" aria-labelledby="pipeline-h">
      <div className="section-head">
        <h2 id="pipeline-h" className="label">
          Pipeline
        </h2>
        <span className="label figures">
          {pad(active.length)} in process · {pad(placed.length)} placed
        </span>
      </div>
      <ErrorNotice error={act.error} />

      <div className="board" role="list" aria-label="Pipeline stages">
        {columns.map((col, ci) => {
          const cards = active.filter((c) => c.stageId === col.id);
          return (
            <section key={col.id ?? "shortlisted"} className="column" role="listitem" aria-label={`${col.name}, ${cards.length}`}>
              <h3 className="column-head">
                <span className="label">{col.name}</span>
                <span className="label figures">{pad(cards.length)}</span>
              </h3>
              <ol className="cards">
                {cards.map((card) => (
                  <li key={card.id} className="card">
                    <Link className="card-name" to={`/applications/${card.id}`}>
                      {card.candidate.fullName}
                    </Link>
                    {(card.candidate.currentTitle || card.candidate.currentEmployer) && (
                      <span className="card-line">
                        {[card.candidate.currentTitle, card.candidate.currentEmployer && `at ${card.candidate.currentEmployer}`]
                          .filter(Boolean)
                          .join(" ")}
                      </span>
                    )}
                    {card.nextInterview && <span className="card-line card-interview">Interview {cardWhen.format(new Date(card.nextInterview))}</span>}
                    <span className="card-foot">
                      <span className="figures" title="Time in this stage">
                        {daysSince(card.stageEnteredAt)}
                      </span>
                      {card.candidate.hasCv && <span className="label">CV</span>}

                      {!card.candidate.erased && (
                      <span className="card-moves">
                        <button
                          type="button"
                          className="link-btn"
                          disabled={ci === 0 || act.pending}
                          onClick={() => move(card, -1)}
                          aria-label={`Move ${card.candidate.fullName} back to ${columns[ci - 1]?.name ?? ""}`}
                        >
                          ←
                        </button>
                        <button
                          type="button"
                          className="link-btn"
                          disabled={ci === columns.length - 1 || act.pending}
                          onClick={() => move(card, 1)}
                          aria-label={`Move ${card.candidate.fullName} on to ${columns[ci + 1]?.name ?? ""}`}
                        >
                          →
                        </button>
                      </span>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          );
        })}
        <section className="column placed" role="listitem" aria-label={`Placed, ${placed.length}`}>
          <h3 className="column-head">
            <span className="label">Placed</span>
            <span className="label figures">{pad(placed.length)}</span>
          </h3>
          <ol className="cards">
            {placed.map((card) => (
              <li key={card.id} className="card">
                <Link className="card-name" to={`/applications/${card.id}`}>
                  {card.candidate.fullName}
                </Link>
                {board.placements[card.id] && (
                  <span className="card-line">starts {formatDay(board.placements[card.id]!.startDate)}</span>
                )}
              </li>
            ))}
          </ol>
        </section>
      </div>
      <p className="hint">Open a card to add feedback, reject, or record a placement.</p>

      {out.length > 0 && (
        <details className="earlier">
          <summary className="label">Rejected or withdrawn · {out.length}</summary>
          <ol className="index">
            {out.map((card, i) => (
              <li key={card.id} className="inactive">
                <span className="num" aria-hidden>
                  {pad(i + 1)}
                </span>
                <span className="main">
                  <Link className="primary" to={`/applications/${card.id}`}>
                    {card.candidate.fullName}
                  </Link>
                  <span className="secondary">{card.status}</span>
                </span>
                <span className="actions" />
              </li>
            ))}
          </ol>
        </details>
      )}

      {open ? (
        <AddCandidate jobId={job.id} onBoard={new Set(board.cards.map((c) => c.candidate.id))} onAdded={() => void reload()} />
      ) : (
        <p className="hint spaced-top">This job is {job.status === "filled" ? "filled" : "closed"}. Reopen it to add candidates.</p>
      )}
    </section>
  );
}

function AddCandidate({ jobId, onBoard, onAdded }: { jobId: string; onBoard: Set<string>; onAdded: () => void }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<CandidateSummary[]>([]);
  const search = useAction();
  const add = useAction();

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setResults([]);
      return;
    }
    let current = true;
    const t = setTimeout(() => {
      void search.run(async () => {
        const res = await api.candidates({ q: query });
        if (current) setResults(res.candidates);
      });
    }, 250);
    return () => {
      current = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <div className="add-to-job">
      <label className="field">
        <span className="label">Add a candidate</span>
        <input type="search" autoComplete="off" placeholder="Search your candidates by name, title or employer" value={q} onChange={(e) => setQ(e.target.value)} />
      </label>
      <ErrorNotice error={search.error ?? add.error} />
      {results.length > 0 && (
        <ol className="index">
          {results.slice(0, 8).map((c, i) => (
            <li key={c.id}>
              <span className="num" aria-hidden>
                {pad(i + 1)}
              </span>
              <span className="main">
                <span className="primary">{c.fullName}</span>
                <span className="secondary">{candidateLine(c)}</span>
              </span>
              <span className="actions">
                {onBoard.has(c.id) ? (
                  <span className="label">On this job</span>
                ) : (
                  <button
                    type="button"
                    className="link-btn"
                    disabled={add.pending}
                    onClick={() =>
                      void add.run(async () => {
                        await api.addToJob(jobId, c.id);
                        setQ("");
                        onAdded();
                      })
                    }
                  >
                    Add to shortlist
                  </button>
                )}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
